"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import {
  ArrowLeftRight,
  BarChart3,
  Columns3,
  Download,
  Home,
  Inbox,
  Settings,
  Shield,
  Users,
} from "lucide-react";

const ITENS_DIRETOR = [
  { href: "/admin/inicio", label: "Início", Icon: Home },
  { href: "/admin/pipeline", label: "Pipeline", Icon: Columns3 },
  { href: "/admin/baixar-clientes", label: "Baixar clientes", Icon: Download },
  { href: "/admin/equipe", label: "Equipe", Icon: BarChart3 },
  { href: "/admin", label: "Usuários", Icon: Users },
  { href: "/migracao", label: "Migrações", Icon: ArrowLeftRight },
  { href: "/solicitacoes", label: "Solicitações", Icon: Inbox },
  { href: "/configuracoes", label: "Configurações", Icon: Settings },
];

const DICAS = [
  "Diretoria forte organiza prioridade antes de cobrar velocidade.",
  "A visão do diretor melhora quando ele acompanha tendência, não só urgência.",
  "Gestores rendem melhor quando a cobrança vem com direção clara.",
  "Uma diretoria saudável identifica gargalos cedo e evita efeito cascata.",
  "Boa liderança de topo simplifica a rotina de quem executa.",
  "Resultado previsível nasce de acompanhamento constante.",
  "Quando o diretor olha a equipe toda, a decisão fica mais justa.",
  "Cobrança boa começa pela leitura correta do cenário.",
  "Direção clara reduz ruído entre gestores e consultores.",
  "Ritmo da operação melhora quando o topo sustenta consistência.",
  "Gestor alinhado responde mais rápido ao que a diretoria prioriza.",
  "A melhor intervenção é a que chega antes do atraso virar padrão.",
  "Diretoria eficiente transforma indicador em ação prática.",
  "Acompanhamento diário evita surpresas no fechamento.",
  "Quem lidera times precisa enxergar volume, ritmo e risco ao mesmo tempo.",
  "Uma equipe forte precisa de metas claras e retorno objetivo.",
  "O diretor ganha força quando mantém o foco no que move a operação.",
  "Decisão boa é decisão simples, clara e sustentada por acompanhamento.",
  "A operação cresce melhor quando a liderança mantém o mesmo padrão todos os dias.",
  "Diretoria bem posicionada antecipa problema em vez de correr atrás dele.",
];

function dicaInicial() {
  const dia = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "numeric",
  }).format(new Date());

  return DICAS[(Number(dia) || 1) % DICAS.length];
}

export function AdminDesktopSidebar({ pathname, integrado = false }: { pathname: string; integrado?: boolean }) {
  const [dicaAtual, setDicaAtual] = useState(dicaInicial);
  const [organizacaoId, setOrganizacaoId] = useState("");

  useEffect(() => {
    queueMicrotask(() => setOrganizacaoId(new URLSearchParams(window.location.search).get("organizacaoId")?.trim() || ""));
  }, [pathname]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setDicaAtual((atual) => {
        const indice = Math.max(0, DICAS.indexOf(atual));
        return DICAS[(indice + 1) % DICAS.length];
      });
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  const itemAtivo = (href: string) => (href === "/admin" ? pathname === href : pathname === href || pathname.startsWith(href + "/"));

  return (
    <aside className={`hidden lg:block lg:flex-shrink-0 ${integrado ? "lg:w-[185px]" : "lg:w-[203px]"}`}>
      <div
        data-admin-desktop-sidebar="true"
        className={`sticky flex flex-col bg-[var(--surface-sidebar)] px-4 py-5 ${integrado ? "top-0 h-dvh border-0 shadow-none" : "top-8 h-[calc(100dvh-4rem)] rounded-[24px] border border-[var(--border)] shadow-[var(--shadow-soft)]"}`}
      >
        <div>
          <div className={`flex items-center gap-2 pb-4 ${integrado ? "" : "border-b border-[var(--border-subtle)]"}`}>
            <div data-admin-desktop-brand="true" className="flex h-11 w-11 items-center justify-center rounded-[14px] text-sm font-bold text-white">
              BP
            </div>
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold text-[var(--text-primary)]">Acompanhamento</p>
              <p className="text-[11px] text-[var(--text-soft)]">Diretoria</p>
            </div>
          </div>

          <nav className="mt-5 space-y-2">
            {ITENS_DIRETOR.map(({ href, label, Icon }) => (
              <Link
                key={href}
                href={organizacaoId && href.startsWith("/admin")
                  ? `${href}?organizacaoId=${encodeURIComponent(organizacaoId)}`
                  : href}
                prefetch
                className={`flex h-9 w-full items-center gap-2 rounded-[12px] px-2.5 text-[12.5px] font-medium transition-colors ${
                  itemAtivo(href) ? "bg-[var(--surface-active)] text-[var(--text-primary)]" : "text-[var(--text-secondary)] hover:bg-[var(--surface-card-2)]"
                }`}
              >
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[9px] bg-[var(--surface-card-2)]">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 truncate">{label}</span>
              </Link>
            ))}
          </nav>
        </div>

        <div className="mt-auto space-y-4">
          <div className="rounded-[18px] border border-[var(--border)] bg-[var(--surface-card-2)] px-3 py-4 text-center">
            <div data-admin-desktop-tip-icon="true" className="mx-auto flex h-8 w-8 items-center justify-center rounded-[10px]">
              <Shield className="h-4 w-4" />
            </div>
            <p className="mt-2.5 text-[14px] font-semibold leading-tight text-[var(--text-primary)]">Dica Bem Protege</p>
            <p className="mt-2 line-clamp-4 text-[11px] leading-5 text-[var(--text-secondary)]">{dicaAtual}</p>
          </div>
        </div>
      </div>
    </aside>
  );
}
